<template>
  <div>
    <PageHeaderBanner>
      <template #title>納期一覧</template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <div class="cmd00500-result-table">
      <div
        :class="
          'chem-table-area btn-exist table-top-button ' + (rows.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-delivery-table"
          :rows="rows"
          :columns="columns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <template #top>
            <div class="add-btn-area flex items-center filter-select">
              <q-btn icon="mdi-plus" class="add-btn" color="primary" @click="registerDeliveryTime"
                >追加</q-btn
              >
            </div>
          </template>
          <!-- 編集削除Header -->
          <template #header-cell-editDelete>
            <q-th class="table-btn-area delete-btn-exists"></q-th>
          </template>
          <!-- 編集、削除Body -->
          <template #body-cell-editDelete="cell">
            <q-td v-if="!hideNoDataFlg" class="table-btn-area delete-btn-exists">
              <div class="flex">
                <q-btn
                  icon="mdi-pencil"
                  color="primary"
                  @click="editDeliveryTime(cell.row.standard_delivery_time_code)"
                >
                  編集
                </q-btn>
                <q-btn
                  label="削除"
                  icon="mdi-close"
                  class="deletebtn"
                  color="primary"
                  outline
                  style="color: var(--q-delete)"
                  @click="deleteConfDialog(cell.row.standard_delivery_time_code)"
                />
              </div>
            </q-td>
          </template>
          <!-- 単位(日)表示用Body -->
          <template #body-cell-standard_delivery_time_days="cell">
            <q-td v-if="!hideNoDataFlg"
              >{{ cell.row.standard_delivery_time_days }}日</q-td
            ></template
          >
        </q-table>
        <AlertDialog
          :display="dialogDisplayFlg"
          :level="DIALOG_LEVEL"
          :confirmation="dialogConfFlg"
          :title="dialogTitle"
          :text="dialogText"
          @complete="dialogComplete()"
          @confirm="deleteDeliveryTime()"
          @close="dialogDisplayFlg = false"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import axios from '../../common/axios-common';

import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';

const router = useRouter();
// 削除ボタンが押された基準納期コード
const deleteDeliveryTimeCode = ref('');
// ダイアログのdisplayFlg(表示判断)
const dialogDisplayFlg = ref(false);
// ダイアログのlevel(informationで固定)
const DIALOG_LEVEL = 'information';
// ダイアログのtitle
const dialogTitle = ref('');
// ダイアログのtext(配列にすることでXSSの対策を行う)
const dialogText = ref(['']);
// ダイアログのconfirmation(trueならキャンセルボタンを表示しない)
const dialogConfFlg = ref(false);
// エラーメッセージ表示フラグ
const isMessageChip = ref(false);
// エラーメッセージ
const errMessage = ref('');
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

interface MDeliveryTime {
  described_number: string;
  standard_delivery_time_code: string;
  standard_delivery_time_days: string;
  standard_delivery_time_name: string;
  create_user_name: string;
  create_timestamp: string;
  update_user_name: string;
  update_timestamp: string;
}

// カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns = ref<any>([
  {
    name: 'editDelete',
    label: '',
  },
  {
    name: 'described_number',
    label: '記載No.',
    align: 'left',
    field: (row: MDeliveryTime) => row.described_number,
    sortable: true,
  },
  {
    name: 'standard_delivery_time_code',
    label: '基準納期（種別）',
    align: 'left',
    field: (row: MDeliveryTime) => row.standard_delivery_time_code,
    sortable: true,
  },
  {
    name: 'standard_delivery_time_days',
    label: '基準納期（日数）',
    align: 'left',
    field: (row: MDeliveryTime) => Number(row.standard_delivery_time_days),
    sortable: true,
  },
  {
    name: 'standard_delivery_time_name',
    label: '基準納期名称',
    align: 'left',
    field: (row: MDeliveryTime) => row.standard_delivery_time_name,
    sortable: true,
  },
  {
    name: 'create_user_name',
    label: '登録者',
    align: 'left',
    field: (row: MDeliveryTime) => row.create_user_name,
    sortable: true,
  },
  {
    name: 'create_timestamp',
    label: '登録日時',
    align: 'left',
    field: (row: MDeliveryTime) => row.create_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
  {
    name: 'update_user_name',
    label: '更新者',
    align: 'left',
    field: (row: MDeliveryTime) => row.update_user_name,
    sortable: true,
  },
  {
    name: 'update_timestamp',
    label: '更新日時',
    align: 'left',
    field: (row: MDeliveryTime) => row.update_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
]);

const rows = ref([
  {
    described_number: '',
    standard_delivery_time_code: '',
    standard_delivery_time_days: '',
    standard_delivery_time_name: '',
    create_user_name: '',
    create_timestamp: '',
    update_user_name: '',
    update_timestamp: '',
  },
]);

/**
 * エラー初期化
 */
const resetErrors = () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

// 納期新規登録に遷移
const registerDeliveryTime = () => {
  router.push({ name: 'deliveryTimeRegister', params: { mode: '0' } });
};

// 納期情報変更に遷移
const editDeliveryTime = (deliveryTimeCode: string) => {
  router.push({ name: 'deliveryTimeEdit', params: { mode: '1', deliveryTimeCode } });
};

// 基準納期一覧取得処理
const viewDeliveryTime = async () => {
  resetErrors();
  // Loading Start
  Loading.show();

  // 基準納期一覧を取得するController呼び出し
  await axios
    .post('/cmd005001/view')
    .then((res) => {
      const { common, deliveryTimeList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          rows.value = deliveryTimeList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          // エラー画面へ遷移
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

// 削除確認ダイアログのOKボタン押下時の処理
const deleteDeliveryTime = async () => {
  resetErrors();
  // Loading Start
  Loading.show();
  // 削除する基準納期コードをformDataへ
  const formData = new FormData();
  formData.append('standard_delivery_time_code', deleteDeliveryTimeCode.value);

  // 基準納期削除処理
  await axios
    .post('/cmd005001/delete', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 削除確認ダイアログのtitleを設定
          dialogTitle.value = '削除完了';
          // 削除確認ダイアログのtextを生成
          dialogText.value = [`基準納期を削除しました。`];
          // ダイアログのボタン数判断をtrue(1つ)に
          dialogConfFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.DELETE_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              // ダイアログ表示判断をfalse(非表示)に変更
              dialogDisplayFlg.value = false;
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// 削除確認のダイアログを表示
const deleteConfDialog = (deliveryTimeCode: string) => {
  deleteDeliveryTimeCode.value = deliveryTimeCode;

  // 削除確認ダイアログのtitleを設定
  dialogTitle.value = '削除確認';
  // 削除確認ダイアログのtextを生成
  dialogText.value = [
    `基準納期 ${deleteDeliveryTimeCode.value} を削除します。`,
    `よろしいですか？`,
  ];
  // ダイアログのボタン数判断をfalse(2つ)に
  dialogConfFlg.value = false;
  // ダイアログ表示判断をtrue(表示)に変更
  dialogDisplayFlg.value = true;
};

// 削除完了ダイアログのOKボタン押下時の処理
const dialogComplete = async () => {
  // ダイアログ表示判断をfalse(非表示)に変更
  dialogDisplayFlg.value = false;
  // 一覧を再度表示
  viewDeliveryTime();
};

// 初期表示処理呼び出し
viewDeliveryTime();
</script>
<style scoped>
.cmd00500-result-table {
  width: 90%;
  margin: 0 auto;
}
</style>

<style lang="sass">
.my-sticky-header-column-delivery-table
  /* height or max-height is important */
  max-height: 590px

  /* specifying max-width so the example can
    highlight the sticky column on any browser window */

  td:first-child
    /* bg color is important for td; just specify one */
    background-color: #fff

  tr th
    position: sticky
    /* higher than z-index for td below */
    z-index: 2
    /* bg color is important; just specify one */
    background: #fff

  /* this will be the loading indicator */
  thead tr:last-child th
    /* height of all previous header rows */
    top: 48px
    /* highest z-index */
    z-index: 3
  thead tr:first-child th
    top: 0
    z-index: 1
  tr:first-child th:first-child
    /* highest z-index */
    z-index: 3

  td:first-child
    z-index: 1

  td:first-child, th:first-child
    position: sticky
    left: 0

  /* prevent scrolling behind sticky top row on focus */
  tbody
    /* height of all previous header rows */
    scroll-margin-top: 48px
</style>
